import {
  inject,
  provideAppInitializer,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { UserService } from './user.service';
import { environment, shouldLogTranslation } from '@camino-solutions/core/environment';

/**
 * Check if a translation log should be shown based on environment config.
 */
function shouldLog(level: 'error' | 'warning' | 'info'): boolean {
  return environment.isAngularDevMode && shouldLogTranslation(environment.debugTranslations, level);
}

export function userAppInitializerFactory(userService: UserService): () => Promise<unknown> {
  const translocoService = inject(TranslocoService);

  return () =>
    userService.initApp().then(({ profile: { lcid } }) => {
      const language = lcid.length === 0 ? 'hu' : lcid;
      translocoService.setActiveLang(language);
      return true;
    });
}

export function provideUserInitializer(): EnvironmentProviders | Provider {
  return provideAppInitializer(async () => {
    const translocoService = inject(TranslocoService);
    const userService = inject(UserService);

    if (shouldLog('info')) {
      console.log('[UserService] Fetching user profile...');
    }

    const { profile } = await userService.initApp();
    const language = profile.lcid.length === 0 ? 'hu' : profile.lcid;

    if (shouldLog('info')) {
      console.log(`[UserService] User profile loaded, language preference: ${language}`);
    }

    // Set the active language and wait for translations to load completely
    translocoService.setActiveLang(language);

    if (shouldLog('info')) {
      console.log(`[UserService] Loading translations for: ${language}`);
    }

    // Wait for translations to fully load before allowing app to render
    await firstValueFrom(translocoService.load(language));

    if (shouldLog('info')) {
      console.log(`[UserService] ✅ Translations loaded successfully for: ${language}`);
    }

    return true;
  });
}
